import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import Input from '../components/elements/Input';
import { getInvoiceById } from '../services/invoices';
import toast from 'react-hot-toast';
import { PDFViewer } from '@react-pdf/renderer';
import InvoicePdf from '../components/pdf/InvoicePdf';
import InvoicePdfButton from '../components/pdf/InvoicePdfButton';

const InvoiceView = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();

  const [invoiceData, setInvoiceData] = useState({
    date: '',
    client_name: '',
    address: '',
    phone: '',
    mobile: '',
    comments: '',
    sub_total: '0',
    vat_percentage: '20',
    vat_total: '0',
    grand_total: '0',
    status: 'draft'
  });
  const [loading, setLoading] = useState(true);
  const [showPdfPreview, setShowPdfPreview] = useState(false);

  // Fetch invoice data
  useEffect(() => {
    const fetchInvoiceData = async () => {
      try {
        setLoading(true);
        const invoice = await getInvoiceById(invoiceId);

        const formattedData = {
          ...invoice,
          date: invoice.date ? invoice.date.split('T')[0] : '',
          sub_total: invoice.sub_total?.toString() || '0',
          vat_percentage: invoice.vat_percentage?.toString() || '20',
          vat_total: invoice.vat_total?.toString() || '0',
          grand_total: invoice.grand_total?.toString() || '0'
        };

        setInvoiceData(formattedData);

      } catch (error) {
        toast.error(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (invoiceId) {
      fetchInvoiceData();
    }
  }, [invoiceId]);

  if (loading) {
    return <DashboardLayout pageTitle="Loading..."><p className='dark:text-white'>Loading...</p></DashboardLayout>;
  }

  return (
    <DashboardLayout pageTitle="View Invoice">
      <div className="max-w-4xl mx-auto">
        <div className='flex justify-between'>
          <div className='mb-4'>
            <button onClick={() => navigate(-1)} className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-md shadow ml-4">
              Back
            </button>
          </div>
          {showPdfPreview && (
            <div className="text-right">
              <button
                type="button"
                onClick={() => setShowPdfPreview(!showPdfPreview)}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-md shadow text-sm sm:text-base"
              >
                Hide Preview
              </button>
            </div>
          )}
        </div>
        {showPdfPreview && invoiceData && (
          <div style={{ height: '80vh' }} className="mb-4 text-right">
            <PDFViewer width="100%" height="100%">
              <InvoicePdf data={invoiceData} />
            </PDFViewer>
          </div>
        )}
        <div className={showPdfPreview ? "h-10" : ""}></div>
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-4 sm:p-8 mb-10">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 dark:text-white">
              View Invoice
            </h1>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              <Input
                label="Date"
                type="date"
                name="date"
                value={invoiceData.date}
                readOnly
                className="bg-gray-100 dark:bg-gray-600 cursor-not-allowed"
              />

              <Input
                label="Client Name"
                type="text"
                name="client_name"
                value={invoiceData.client_name}
                readOnly
                className="bg-gray-100 dark:bg-gray-600 cursor-not-allowed"
              />

              <div>
                <label className="block font-semibold mb-1 text-gray-800 dark:text-white">
                  Address
                </label>
                <textarea
                  name="address"
                  className="w-full border rounded-md p-2 bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600 cursor-not-allowed"
                  rows="4"
                  value={invoiceData.address}
                  readOnly
                ></textarea>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              <Input
                label="Phone"
                type="tel"
                name="phone"
                value={invoiceData.phone}
                readOnly
                className="bg-gray-100 dark:bg-gray-600 cursor-not-allowed"
              />

              <Input
                label="Mobile"
                type="tel"
                name="mobile"
                value={invoiceData.mobile}
                readOnly
                className="bg-gray-100 dark:bg-gray-600 cursor-not-allowed"
              />

              <div>
                <label className="block font-semibold mb-1 text-gray-800 dark:text-white">
                  Comments
                </label>
                <textarea
                  name="comments"
                  className="w-full border rounded-md p-2 bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600 cursor-not-allowed"
                  rows="4"
                  value={invoiceData.comments}
                  readOnly
                ></textarea>
              </div>
            </div>
          </div>

          {/* Totals Section */}
          <div className="mt-6 pt-6 border-t border-gray-300 dark:border-gray-600">
            <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white">Invoice Totals</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Input
                label="Sub Total (£)"
                type="number"
                name="sub_total"
                value={invoiceData.sub_total}
                readOnly
                className="bg-gray-100 dark:bg-gray-600 cursor-not-allowed"
              />

              <Input
                label="VAT Percentage (%)"
                type="number"
                name="vat_percentage"
                value={invoiceData.vat_percentage}
                readOnly
                className="bg-gray-100 dark:bg-gray-600 cursor-not-allowed"
              />

              <Input
                label="VAT Total (£)"
                type="number"
                name="vat_total"
                value={invoiceData.vat_total}
                readOnly
                className="bg-gray-100 dark:bg-gray-600 cursor-not-allowed"
              />

              <Input
                label="Grand Total (£)"
                type="number"
                name="grand_total"
                value={invoiceData.grand_total}
                readOnly
                className="bg-gray-100 dark:bg-gray-600 font-semibold cursor-not-allowed"
              />
            </div>
          </div>

          {/* Status Field */}
          <div className="mt-6">
            <label className="block font-semibold mb-1 text-gray-800 dark:text-white">Status</label>
            <select
              name="status"
              value={invoiceData.status}
              readOnly
              disabled
              className="w-full border rounded-md p-2 bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600 cursor-not-allowed"
            >
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div className="flex justify-end mt-8">
            <InvoicePdfButton data={invoiceData} />
            <button
              type="button"
              onClick={() => setShowPdfPreview(!showPdfPreview)}
              className="mx-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-md shadow text-sm sm:text-base"
            >
              {!showPdfPreview ? 'Show PDF Preview' : "Hide PDF Preview"}
            </button>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="bg-gray-600 hover:bg-gray-700 text-white font-semibold px-6 py-2 rounded-md shadow text-sm sm:text-base"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout >
  );
};

export default InvoiceView;